/**
 * mesh-flow.js
 *
 * The animated WebGL backdrop behind the Move hero. Rendered by
 * snippets/gradient-wave.liquid with effect: 'mesh', which owns the
 * markup, the first-paint CSS gradient underneath it and the colors
 * this file reads.
 *
 * A port of a fragment-shader mesh: fractal Brownian motion over
 * simplex noise, pushed around by a sine domain warp and a swirl that
 * tightens toward the center, then quantised into a 20-step palette
 * ramp. Unlike assets/gradient-wave.js, which bends a real mesh in the
 * vertex shader, every pixel here is computed independently in the
 * fragment shader — the geometry is one full-screen quad and all of the
 * cost is per-pixel.
 *
 * Same three storefront concessions as gradient-wave.js: it never
 * blocks the hero, it stops animating when nobody is looking at it, and
 * it honours prefers-reduced-motion by drawing one frame. See that
 * file's header for why each of those matters.
 *
 * Two differences worth knowing:
 *
 * - This needs WebGL2, for the GLSL ES 3.00 array indexing the palette
 *   ramp uses. gradient-wave.js runs on WebGL1. Anything that cannot
 *   give a webgl2 context keeps the static CSS gradient, same as any
 *   other failure here.
 * - The palette arrives as a handful of stops and is expanded to 20
 *   evenly spaced steps here, so the calling section passes the same
 *   short hex list it passes for the wave effect.
 */
(function () {
  'use strict';

  /* Multiplies the elapsed time before it reaches the shader, which
     slows the domain warp, the swirl and the noise drift together in
     proportion. Matches SPEED in assets/gradient-wave.js so the two
     backdrops move at the same pace across the site. */
  var SPEED = 0.6;

  /* Steps in the quantised palette ramp. The reference uses 20 and the
     banding it produces at that count is the look — drop it much lower
     and the bands read as posterisation rather than depth. */
  var RAMP_STEPS = 20;

  /* Octaves of noise summed per pixel, down from the reference's 10.
     Each octave doubles the frequency and halves the amplitude, so past
     about seven the contribution is smaller than one step of the
     palette ramp and cannot be seen — but it is still a full simplex
     evaluation per pixel per frame, which on a phone is the difference
     between this holding 60fps and not. */
  var FBM_OCTAVES = 7;

  /* Shader constants, carried over from the reference unchanged. Time
     is scaled before it arrives (see SPEED), so these stay at their
     original values rather than each being retuned. */
  var ZOOM = 0.3;
  var WAVE_AMPLITUDE = 0.2;
  var WAVE_RANDOMNESS = 0.15;
  var WAVE_FREQUENCY = 4.0;
  var TIME_FACTOR = 0.25;
  var SWIRL_STRENGTH = 1.2;
  var SWIRL_TIME_MULT = 5.0;
  var NOISE_SWIRL = 0.2;

  var MAX_FRAME_MS = 1000 / 15;

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (error) {
      return false;
    }
  }

  function parseColor(hex) {
    var value = parseInt(String(hex).replace('#', ''), 16);
    return [
      ((value >> 16) & 255) / 255,
      ((value >> 8) & 255) / 255,
      (value & 255) / 255
    ];
  }

  /**
   * Expands a handful of palette stops into RAMP_STEPS evenly spaced
   * colors by walking the stops and interpolating between neighbours.
   * The shader samples this ramp by noise value, so the stops end up
   * ordered darkest to lightest by position rather than by brightness —
   * whatever order the caller passes is the order the ramp climbs in.
   */
  function buildRamp(stops) {
    var colors = stops.map(parseColor);
    if (colors.length === 1) colors.push(colors[0]);

    var ramp = [];
    for (var i = 0; i < RAMP_STEPS; i++) {
      var position = (i / (RAMP_STEPS - 1)) * (colors.length - 1);
      var low = Math.floor(position);
      var high = Math.min(low + 1, colors.length - 1);
      var blend = position - low;
      ramp.push([
        colors[low][0] + (colors[high][0] - colors[low][0]) * blend,
        colors[low][1] + (colors[high][1] - colors[low][1]) * blend,
        colors[low][2] + (colors[high][2] - colors[low][2]) * blend
      ]);
    }
    return ramp;
  }

  var VERTEX_SHADER = [
    '#version 300 es',
    'precision mediump float;',
    'in vec2 aPosition;',
    'void main() {',
    '  gl_Position = vec4(aPosition, 0.0, 1.0);',
    '}'
  ].join('\n');

  function buildFragmentShader(ramp) {
    var paletteSource = ramp
      .map(function (color) {
        return '  vec3(' + color[0].toFixed(4) + ', ' + color[1].toFixed(4) + ', ' + color[2].toFixed(4) + ')';
      })
      .join(',\n');

    return [
      /* Must be the very first characters in the source — a leading
         newline or a comment above it is a compile error in GLSL ES
         3.00, which is why this array starts here rather than with a
         banner. */
      '#version 300 es',
      'precision highp float;',
      'out vec4 outColor;',
      '',
      'uniform vec2 uResolution;',
      'uniform float uTime;',
      '',
      '#define NUM_COLORS ' + ramp.length,
      '',
      'const vec3 palette[NUM_COLORS] = vec3[](',
      paletteSource,
      ');',
      '',
      'vec3 permute(vec3 x) {',
      '  return mod(((x * 34.0) + 1.0) * x, 289.0);',
      '}',
      '',
      'float noise2D(vec2 v) {',
      '  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);',
      '  vec2 i = floor(v + dot(v, C.yy));',
      '  vec2 x0 = v - i + dot(i, C.xx);',
      '  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);',
      '  vec4 x12 = x0.xyxy + C.xxzz;',
      '  x12.xy -= i1;',
      '  i = mod(i, 289.0);',
      '  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));',
      '  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);',
      '  m = m * m;',
      '  m = m * m;',
      '  vec3 x = 2.0 * fract(p * C.www) - 1.0;',
      '  vec3 h = abs(x) - 0.5;',
      '  vec3 ox = floor(x + 0.5);',
      '  vec3 a0 = x - ox;',
      '  m *= 1.792843 - 0.853734 * (a0 * a0 + h * h);',
      '  vec3 g;',
      '  g.x  = a0.x  * x0.x  + h.x  * x0.y;',
      '  g.yz = a0.yz * x12.xz + h.yz * x12.yw;',
      '  return 130.0 * dot(m, g);',
      '}',
      '',
      'float fbm(vec2 st) {',
      '  float value = 0.0;',
      '  float amplitude = 0.5;',
      '  float freq = 1.0;',
      '  for (int i = 0; i < ' + FBM_OCTAVES + '; i++) {',
      '    value += amplitude * noise2D(st * freq);',
      '    freq *= 2.0;',
      '    amplitude *= 0.5;',
      '  }',
      '  return value;',
      '}',
      '',
      'void main() {',
      '  vec2 uv = (gl_FragCoord.xy / uResolution.xy) * 2.0 - 1.0;',
      '  uv.x *= uResolution.x / uResolution.y;',
      '  uv *= float(' + ZOOM + ');',
      '',
      '  float t = uTime * float(' + TIME_FACTOR + ');',
      '',
      /* The warp amplitude is itself driven by noise, so the waves
         breathe instead of running at one fixed depth. */
      '  float waveAmp = float(' + WAVE_AMPLITUDE + ') + float(' + WAVE_RANDOMNESS + ') * noise2D(vec2(t, 27.7));',
      '  uv.x += waveAmp * sin(uv.y * float(' + WAVE_FREQUENCY + ') + t);',
      '  uv.y += waveAmp * sin(uv.x * float(' + WAVE_FREQUENCY + ') - t);',
      '',
      /* The swirl falls off to nothing by the edge of the unit circle,
         so the corners stay still and the motion pools in the middle,
         behind the copy. */
      '  float r = length(uv);',
      '  float angle = atan(uv.y, uv.x);',
      '  float swirl = float(' + SWIRL_STRENGTH + ') * (1.0 - smoothstep(0.0, 1.0, r));',
      '  angle += swirl * sin(uTime + r * float(' + SWIRL_TIME_MULT + '));',
      '  uv = vec2(cos(angle), sin(angle)) * r;',
      '',
      '  float n = fbm(uv);',
      '  n += float(' + NOISE_SWIRL + ') * sin(t + n * 3.0);',
      '',
      '  float noiseVal = 0.5 * (n + 1.0);',
      '  float idx = clamp(noiseVal, 0.0, 1.0) * float(NUM_COLORS - 1);',
      '  int iLow = int(floor(idx));',
      '  int iHigh = int(min(float(iLow + 1), float(NUM_COLORS - 1)));',
      '',
      /* Mixing between neighbouring steps rather than snapping to one
         keeps the bands soft-edged. The reference makes the darkest
         step fully transparent to let the page show through; here the
         static gradient behind the canvas is already that same dark
         tone, so a transparent step would only make the cross-fade
         visible. Every pixel is opaque. */
      '  vec3 color = mix(palette[iLow], palette[iHigh], fract(idx));',
      '  outColor = vec4(color, 1.0);',
      '}'
    ].join('\n');
  }

  function compile(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      var log = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(log);
    }
    return shader;
  }

  function MeshFlow(canvas, stops) {
    var gl = canvas.getContext('webgl2', { alpha: false, antialias: false });
    if (!gl) throw new Error('WebGL2 is not available');

    this.canvas = canvas;
    this.gl = gl;
    this.time = 0;
    this.last = 0;
    this.playing = false;
    this.frameId = null;

    var program = gl.createProgram();
    gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER));
    gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, buildFragmentShader(buildRamp(stops))));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(program));
    }

    gl.useProgram(program);
    this.program = program;

    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);

    this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    var position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    this.uResolution = gl.getUniformLocation(program, 'uResolution');
    this.uTime = gl.getUniformLocation(program, 'uTime');

    this.resize();
  }

  MeshFlow.prototype.resize = function () {
    var rect = this.canvas.parentNode.getBoundingClientRect();
    var width = Math.max(1, Math.round(rect.width));
    var height = Math.max(1, Math.round(rect.height));

    if (width === this.width && height === this.height) return;
    this.width = width;
    this.height = height;

    /* Deliberately one canvas pixel per CSS pixel, ignoring
       devicePixelRatio. This is a per-pixel shader with seven octaves of
       noise in it, so rendering at 2x on a phone is four times the work
       for an image that is a soft gradient — there is no detail in it
       fine enough for the extra resolution to resolve. */
    this.canvas.width = width;
    this.canvas.height = height;
    this.gl.viewport(0, 0, width, height);
  };

  MeshFlow.prototype.drawFrame = function (timestamp) {
    this.time += Math.min(timestamp - this.last, MAX_FRAME_MS);
    this.last = timestamp;

    var gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.uniform2f(this.uResolution, this.width, this.height);
    gl.uniform1f(this.uTime, (this.time * 0.001) * SPEED);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  };

  MeshFlow.prototype.start = function () {
    if (this.playing) return;
    this.playing = true;

    var flow = this;
    /* Reset rather than accumulate: the gap since the last frame is
       time the visitor did not see, and folding it in would snap the
       pattern forward the moment the tab comes back. */
    this.last = 0;

    var tick = function (timestamp) {
      if (!flow.playing) return;
      if (!flow.last) flow.last = timestamp;
      flow.drawFrame(timestamp);
      flow.frameId = window.requestAnimationFrame(tick);
    };

    this.frameId = window.requestAnimationFrame(tick);
  };

  MeshFlow.prototype.stop = function () {
    this.playing = false;
    if (this.frameId) window.cancelAnimationFrame(this.frameId);
    this.frameId = null;
  };

  function mount(container) {
    if (container.dataset.meshFlowMounted === 'true') return;

    var stops = (container.getAttribute('data-colors') || '')
      .split(',')
      .map(function (color) {
        return color.trim();
      })
      .filter(Boolean);
    if (!stops.length) return;

    var canvas = document.createElement('canvas');
    canvas.className = 'gradient-wave__canvas';
    container.appendChild(canvas);

    var flow;
    try {
      flow = new MeshFlow(canvas, stops);
    } catch (error) {
      /* No WebGL2, a shader that would not compile, or a context the
         browser refused. The static CSS gradient underneath is already
         on screen and stays there. */
      container.removeChild(canvas);
      return;
    }

    container.dataset.meshFlowMounted = 'true';
    var reduced = prefersReducedMotion();
    var visible = true;
    var observer;

    flow.drawFrame(0);
    window.requestAnimationFrame(function () {
      container.classList.add('gradient-wave--ready');
    });

    function sync() {
      if (reduced) return;
      if (visible && !document.hidden) {
        flow.start();
      } else {
        flow.stop();
      }
    }

    sync();

    var resizeTimer = null;
    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        flow.resize();
        /* The viewport changed under a paused or reduced-motion canvas,
           which would otherwise hold a frame drawn at the old size until
           a tick that never comes. */
        flow.drawFrame(flow.last);
      }, 150);
    }

    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', sync);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        sync();
      });
      observer.observe(container);
    }

    /* The theme editor tears sections down and rebuilds them on every
       setting change. Without this, each edit leaks a WebGL context and
       the browser starts dropping the oldest ones. */
    document.addEventListener('shopify:section:unload', function (event) {
      if (!event.target.contains(container)) return;
      flow.stop();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', sync);
      if (observer) observer.disconnect();
    });
  }

  function init() {
    var containers = document.querySelectorAll('[data-mesh-flow]');
    Array.prototype.forEach.call(containers, mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
